import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { CITATION_STYLES, buildInTextMap, formatBibliographyMarkdown, getStyle, isQuickStyle } from './citations';
import JournalPicker from './JournalPicker';

const GROUPS = [
  { title: 'Numbered', styles: CITATION_STYLES.filter((s) => s.numeric) },
  { title: 'Author–date', styles: CITATION_STYLES.filter((s) => !s.numeric) },
];

/**
 * Choose the citation style for a publication: one of the quick picks or any journal
 * style from the repository. references is the full bibliography (cited first); the
 * preview uses up to three of them. onApply(style, title) persists it.
 */
const FormatReferencesDialog = ({ open, value, valueTitle, references = [], onClose, onApply }) => {
  const [selected, setSelected] = useState(value);
  const [selectedTitle, setSelectedTitle] = useState(valueTitle || '');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => { if (open) { setSelected(value); setSelectedTitle(valueTitle || ''); } }, [open, value, valueTitle]);

  // Sample: the first cited references, or the first ones in the bibliography
  const cited = references.filter((r) => r.cited && r.csl);
  const sample = (cited.length ? cited : references.filter((r) => r.csl).map((r) => ({ ...r, cited: true }))).slice(0, 3);

  useEffect(() => {
    if (!open || !sample.length) { setPreview(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    const marker = `[${sample.map((_, i) => i + 1).join(', ')}]`;
    Promise.all([
      buildInTextMap([marker], sample, selected),
      formatBibliographyMarkdown(sample, selected),
    ])
      .then(([map, bibliography]) => {
        if (!cancelled) setPreview({ inText: map.get(marker) || marker, bibliography });
      })
      .catch(() => { if (!cancelled) setPreview({ error: true }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected, references]);

  const handleApply = async () => {
    setApplying(true);
    try { await onApply(selected, isQuickStyle(selected) ? '' : selectedTitle); } finally { setApplying(false); }
  };

  const style = getStyle(selected, selectedTitle);
  const superscript = /^[⁰¹²³⁴⁵⁶⁷⁸⁹]/.test(preview?.inText || '');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Format references</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <JournalPicker
            allowFree={false}
            label="Journal style"
            value={!isQuickStyle(selected) ? { title: selectedTitle, styleId: selected } : { title: '', styleId: null }}
            onChange={(j) => { if (j.styleId) { setSelected(j.styleId); setSelectedTitle(j.styleTitle); } }}
            helperText="Search the journal you are submitting to (10,000+ styles), or pick a common style below"
          />
        </Box>
        {GROUPS.map((group) => (
          <Box key={group.title} sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary">{group.title}</Typography>
            <Grid container spacing={1}>
              {group.styles.map((s) => {
                const isSelected = s.value === selected;
                return (
                  <Grid item xs={6} sm={4} md={2.4} key={s.value}>
                    <Paper
                      variant="outlined"
                      onClick={() => setSelected(s.value)}
                      sx={{
                        p: 1.25, cursor: 'pointer', height: '100%',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderWidth: isSelected ? 2 : 1,
                        bgcolor: isSelected ? 'action.selected' : 'background.paper',
                        '&:hover': { borderColor: 'primary.main' },
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>{s.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.hint}</Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}

        <Typography variant="overline" color="text.secondary">Preview — {style.label}</Typography>
        <Paper variant="outlined" sx={{ p: 2, minHeight: 120 }}>
          {!sample.length ? (
            <Typography color="text.secondary">Add references in the Bibliography tab to see a preview.</Typography>
          ) : loading || !preview ? (
            <CircularProgress size={20} />
          ) : preview.error ? (
            <Alert severity="error">Could not load this style.</Alert>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                In the text: “…as shown previously{superscript ? '' : ' '}<strong>{preview.inText}</strong>.”
              </Typography>
              {preview.bibliography.split(/\n+/).map((line, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Typography key={i} variant="body2" sx={{ mb: 0.75 }}>{line}</Typography>
              ))}
            </>
          )}
        </Paper>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          While you write, citations appear as [1], [2]… so their numbers stay stable. The chosen style is applied to the
          References section, the Preview and the Word export. Styles from the Citation Style Language project.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleApply} disabled={applying}
          startIcon={applying ? <CircularProgress size={16} /> : undefined}>
          Apply {style.label}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormatReferencesDialog;
